// My profile — self-service editor. Email and role are IdP/admin-managed
// and read-only here; display name is the only mutable field for now.

import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth.js';
import { api } from '../lib/api.js';

const ROLE_LABEL = {
  org_admin:    'Admin',
  org_operator: 'Operator',
  org_ro:       'Read Only',
  verso_admin:  'Platform Admin',
};

export default function Profile({ embedded = false } = {}) {
  const { user, refetch } = useAuth();
  const queryClient = useQueryClient();

  const [name, setName] = useState(user?.name || '');
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (user?.name != null) setName(user.name);
  }, [user?.name]);

  const dirty = (user?.name || '') !== name.trim() && name.trim().length > 0;

  const saveMutation = useMutation({
    mutationFn: (patch) => api.patch('/me', patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me'] });
      refetch?.();
      setToast({ ok: true, message: 'Profile saved' });
      setTimeout(() => setToast(null), 3000);
    },
    onError: (err) => {
      setToast({ ok: false, message: err?.body?.error || err?.message || 'Save failed' });
      setTimeout(() => setToast(null), 4000);
    },
  });

  if (!user) return null;

  return (
    <div className="space-y-6">
      {embedded ? (
        <span className="text-sm text-gray-500">
          Your account. Email and role come from Cloudflare Access + your organization admin.
        </span>
      ) : (
        <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
      )}

      {toast && (
        <div
          className={`rounded-md border px-4 py-2 text-sm ${
            toast.ok
              ? 'border-green-200 bg-green-50 text-green-700'
              : 'border-red-200 bg-red-50 text-red-700'
          }`}
        >
          {toast.message}
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!dirty || saveMutation.isPending) return;
          saveMutation.mutate({ name: name.trim() });
        }}
        className="max-w-xl space-y-4 rounded-lg border border-gray-200 bg-white p-6"
      >
        <div>
          <label className="block text-sm font-medium text-gray-700">Display name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
            required
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Email</label>
          <input
            type="text"
            value={user.email || ''}
            disabled
            className="mt-1 w-full cursor-not-allowed rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm text-gray-500"
          />
          <p className="mt-1 text-xs text-gray-400">Managed by your identity provider.</p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700">Role</label>
            <input
              type="text"
              value={ROLE_LABEL[user.role] || user.role || ''}
              disabled
              className="mt-1 w-full cursor-not-allowed rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm text-gray-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Organization</label>
            <input
              type="text"
              value={user.org_name || user.org_id || ''}
              disabled
              className="mt-1 w-full cursor-not-allowed rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm text-gray-500"
            />
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <button
            type="submit"
            disabled={!dirty || saveMutation.isPending}
            className="rounded-md bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {saveMutation.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
